import {
  CdkDrag,
  CdkDragDrop,
  CdkDragHandle,
  CdkDropList,
  moveItemInArray,
} from '@angular/cdk/drag-drop';
import { BreakpointObserver } from '@angular/cdk/layout';
import { CommonModule } from '@angular/common';
import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  ContentChild,
  ContentChildren,
  ElementRef,
  EventEmitter,
  InjectionToken,
  Input,
  NgZone,
  OnChanges,
  OnInit,
  Output,
  QueryList,
  SimpleChanges,
  TemplateRef,
  ViewChild,
  computed,
  effect,
  inject,
  signal,
  viewChild,
} from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import {
  FormBuilder,
  FormControl,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import {
  EnumOptions,
  filterEnumOptions,
} from '@kinet-open-network/shared/data-access/model';
import { dataGet, errorToString } from '@kinet-open-network/shared/util';
import {
  FilterMatchMode,
  FilterMetadata,
  MenuItem,
  TableState,
} from 'primeng/api';
import { ButtonModule } from 'primeng/button';
import { CheckboxModule } from 'primeng/checkbox';
import { ChipModule } from 'primeng/chip';
import { ContextMenu, ContextMenuModule } from 'primeng/contextmenu';
import { Dialog, DialogModule } from 'primeng/dialog';
import { DividerModule } from 'primeng/divider';
import { InputGroupModule } from 'primeng/inputgroup';
import { InputGroupAddonModule } from 'primeng/inputgroupaddon';
import { InputTextModule } from 'primeng/inputtext';
import { MenuModule } from 'primeng/menu';
import { OverlayPanelModule } from 'primeng/overlaypanel';
import { ProgressBarModule } from 'primeng/progressbar';
import { RadioButtonModule } from 'primeng/radiobutton';
import { SplitButton, SplitButtonModule } from 'primeng/splitbutton';
import {
  Table,
  TableColumnReorderEvent,
  TableLazyLoadEvent,
  TableModule,
  TableRowCollapseEvent,
  TableRowExpandEvent,
} from 'primeng/table';
import { TabViewModule } from 'primeng/tabview';
import { TooltipModule } from 'primeng/tooltip';
import {
  Subject,
  debounceTime,
  distinctUntilChanged,
  from,
  switchMap,
  take,
} from 'rxjs';
import { IButton, confirmDialog } from '../..';
import { BooleanColumnComponent } from '../boolean-column/boolean-column.component';
import { toast } from '../toasts/toasts.signals';
import {
  ALL_TABLE_ACTION_CONTEXTS,
  ActionWithoutContext,
  BulkAction,
  CategorizedActions,
  ProcessedAction,
  TableAction,
  TableActionContext,
} from './resource-actions';
import { ResourceTableColumnFilterComponent } from './resource-table-column-filter.component';
import { ResourceTableColumnDirective } from './resource-table-column.directive';
import {
  RESOURCE_TABLE_PRESET_ADAPTER,
  ResourceTablePreset,
  ResourceTablePresetData,
  tablePresetHash,
} from './resource-table-preset-adapter.service';
import {
  ResourceTableQueryAdapter,
  TableApiQuery,
  TableQuery,
} from './resource-table-query-adapter.service';

export const RESOURCE_TABLE_STATE_KEY_PREFIX = new InjectionToken<string>(
  'Prefix to use for the state key of the resource table',
);

@Component({
  selector: 'kon-resource-table',
  standalone: true,
  imports: [
    CommonModule,
    TableModule,
    FormsModule,
    ButtonModule,
    ReactiveFormsModule,
    RouterLink,
    ContextMenuModule,
    MenuModule,
    TooltipModule,
    SplitButtonModule,
    InputTextModule,
    ProgressBarModule,
    RadioButtonModule,
    OverlayPanelModule,
    CheckboxModule,
    DialogModule,
    TabViewModule,
    ChipModule,
    DividerModule,
    ResourceTableColumnFilterComponent,
    BooleanColumnComponent,
    InputGroupModule,
    InputGroupAddonModule,
    CdkDropList,
    CdkDrag,
    CdkDragHandle,
  ],
  templateUrl: './resource-table.component.html',
  styleUrls: ['./resource-table.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [ResourceTableQueryAdapter],
})
export class ResourceTableComponent<T>
  implements OnInit, OnChanges, AfterViewInit
{
  private breakpointObserver = inject(BreakpointObserver);
  private queryAdapter = inject(ResourceTableQueryAdapter);
  private router = inject(Router);
  private route = inject(ActivatedRoute);
  private stateKeyPrefix =
    inject(RESOURCE_TABLE_STATE_KEY_PREFIX, { optional: true }) ?? '';
  private zone = inject(NgZone);
  private presetAdapter = inject(RESOURCE_TABLE_PRESET_ADAPTER, {
    optional: true,
  });
  private _columns: TableColumns = [];
  asortedColumns: TableColumns = [];
  @Input() set columns(columns: TableColumns) {
    this._columns = columns;
    this.asortedColumns = [...columns];
    this.refreshTableQuery();

    this.syncColumnsToTableState(); // ak pribudnu nove stlpce table ich nema v columnOrder, musime ich pridat manualne
    // primeng neobnovi poradie stlpcov, pokial su stlpce dynamicke, musime to spravit manualne
    setTimeout(() => {
      if (this.dataTable) {
        this.dataTable.restoreColumnOrder();
        this.dataTable.saveState();
      }
    });
  }
  get columns() {
    return this._columns;
  }

  private columnsVisibleState: Record<string, boolean> = {};

  private _data: T[] = [];
  @Input() set data(data: T[] | null | undefined) {
    this._data = data ?? [];
    this.refreshSelectedData();
    this.refreshTableData();
    this.refreshPerPageOptions();
  }
  get data() {
    return this._data;
  }

  private _meta?: TableDataMeta | null = {};
  @Input() set meta(meta: TableDataMeta | null | undefined) {
    this._meta = meta ?? {};
    this.refreshPerPageOptions();
  }
  get meta() {
    return this._meta;
  }

  tableQuery: TableQuery = {};

  private _routeQuery: TableApiQuery | null = null;
  private _query: TableApiQuery | null = null;
  @Input() set query(query: TableApiQuery | null) {
    this._query = query;
    this.refreshPresetHash();
    this.refreshTableQuery();
    this.refreshPerPageOptions();
  }
  get query() {
    return this._query;
  }

  get activeFilters() {
    const filters = this.tableQuery.filters ?? {};
    const activeFilters: TableActiveFilters = {};

    for (const [key, value] of Object.entries(filters)) {
      const column = this.columns.find((c) => c.key === key);

      if (!column) {
        continue;
      }

      const getCountFromFilterMeta = (filterMeta: FilterMetadata) => {
        if (filterMeta.matchMode === FilterMatchMode.IN) {
          return filterMeta.value.length;
        }

        return 1;
      };

      let count = 0;
      const filterMeta = value;

      if (Array.isArray(filterMeta)) {
        count = filterMeta.reduce(
          (acc, f) => acc + getCountFromFilterMeta(f),
          0,
        );
      } else {
        count = filterMeta ? getCountFromFilterMeta(filterMeta) : 0;
      }

      if (count > 0) {
        activeFilters[key] = { label: column.label ?? key, count };
      }
    }

    return activeFilters;
  }

  @Output() queryChange = new EventEmitter<TableApiQuery | null>();
  @Output() rowExpand = new EventEmitter<TableRowExpandEvent>();
  @Output() rowCollapse = new EventEmitter<TableRowExpandEvent>();

  @Input() loading = false;

  @ContentChildren(ResourceTableColumnDirective)
  templates!: QueryList<ResourceTableColumnDirective>;

  @ContentChild('rowexpansion') rowExpansionTemplate?: TemplateRef<any>;

  @ViewChild('contextMenu') contextMenu?: ContextMenu;

  @ViewChild('dataTable') dataTable?: Table;

  @ViewChild('searchInput') searchInput?: ElementRef;

  selectedRow: T | null = null;

  contextMenuItems: MenuItem[] | undefined = undefined;

  tableData: TableProcessedData<T> = [];

  actions: CategorizedActions<T> = {};

  // ─── Bulk selection ─────────────────────────────────────────────────────────

  /** Set of resolved ids for currently selected rows. */
  selectedIds = signal<Set<string>>(new Set());

  bulkActionsRunning = signal(false);

  bulkProgress = signal<{ current: number; total: number } | null>(null);

  private bulkCancellationRequested = false;

  /** Index of the last individually toggled row, used as anchor for shift-selection. */
  private _lastSelectedIndex: number | null = null;

  get hasBulkActions(): boolean {
    return (this.config.bulkActions?.length ?? 0) > 0 && !!this.config.dataKey;
  }

  get allRowsSelected(): boolean {
    return (
      this._data.length > 0 && this.selectedIds().size === this._data.length
    );
  }

  get someRowsSelected(): boolean {
    return (
      this.selectedIds().size > 0 && this.selectedIds().size < this._data.length
    );
  }

  // ────────────────────────────────────────────────────────────────────────────

  searchControl = new FormControl<string>('');

  perPageOptions = [25, 50, 100];

  tableSettingsVisible = signal(false);

  activeSettingsIndex = signal(0);

  showActiveFilters = true;

  showSearch = false;

  private fb = inject(FormBuilder);

  private refreshPresetHashStream$ = new Subject<void>();

  presetNameInput = viewChild<ElementRef<HTMLInputElement>>('presetNameInput');

  newPresetDialogVisible = signal(false);

  newPresetForm = this.fb.nonNullable.group({
    name: ['', Validators.required],
  });

  presetsEnabled = signal(false);

  presetsLoading = signal(false);

  presets = signal<ResourceTablePreset[]>([]);

  sortedPresets = computed(() => {
    // Presets with highest order first
    return [...this.presets()].sort((a, b) => b.order - a.order);
  });

  editingPreset = signal<ResourceTablePreset | null>(null);

  editingPresetValue = signal<{ id: string | null; name: string }>({
    id: this.editingPreset()?.id || null,
    name: this.editingPreset()?.name || '',
  });

  currentPresetHash = toSignal(
    this.refreshPresetHashStream$.asObservable().pipe(
      takeUntilDestroyed(),
      switchMap(() => from(tablePresetHash(this.getCurrentPresetData()))),
    ),
    {
      initialValue: '',
    },
  );

  activePresetHash = signal('');

  activePreset = computed(() => {
    const presets = this.presets();
    const currentHash = this.activePresetHash();

    return presets.find((p) => p.hash === currentHash);
  });

  activePresetDirty = computed(() => {
    const activePreset = this.activePreset();
    const currentHash = this.currentPresetHash();

    return activePreset ? activePreset.hash !== currentHash : false;
  });

  presetsMenuItems = computed<MenuItem[]>(() => {
    const presets = this.sortedPresets();
    const activePreset = this.activePreset();
    const presetDirty = this.activePresetDirty();

    const presetsMenuItems: MenuItem[] = presets.map((preset) => ({
      label: preset.name,
      icon:
        activePreset?.hash === preset.hash
          ? 'pi pi-bookmark-fill'
          : 'pi pi-bookmark',
      styleClass: activePreset?.hash === preset.hash ? 'font-bold' : '',
      command: () => this.applyPreset(preset),
      type: 'preset',
      badge:
        activePreset?.hash === preset.hash && presetDirty
          ? 'Upravené'
          : undefined,
    }));

    return [
      ...(presetsMenuItems.length > 0
        ? [
            {
              separator: true,
            },
            ...presetsMenuItems,
            {
              separator: true,
            },
          ]
        : []),
      ...(presetDirty && activePreset
        ? [
            {
              label: 'Uložiť zmeny',
              icon: 'pi pi-save',
              command: () => this.saveIntoPreset(activePreset),
            },
          ]
        : []),
      {
        label: 'Uložiť ako...',
        icon: 'pi pi-save',
        disabled: !!this.activePreset() && !presetDirty,
        command: () => this.newPresetDialogVisible.set(true),
      },
      {
        label: 'Obnoviť',
        icon: 'pi pi-undo',
        command: () => this.reset(),
      },
      {
        label: 'Nastavenia',
        icon: 'pi pi-cog',
        command: () => this.showTableSettings(2),
      },
    ];
  });

  onPresetReorder(event: CdkDragDrop<ResourceTablePreset>) {
    const currentPresetOrderMap = new Map(
      this.presets().map((preset) => [preset.id, preset.order]),
    );

    const presets = [...this.sortedPresets()];
    const presetsLength = presets.length;
    moveItemInArray(presets, event.previousIndex, event.currentIndex);

    presets.forEach((preset, index) => {
      // highest order first
      preset.order = presetsLength - index;
    });

    this.presets.set(presets);

    const key = this.presetsKey;

    if (!key) {
      return;
    }

    const presetsToSave = presets.filter((preset) => {
      return currentPresetOrderMap.get(preset.id) !== preset.order;
    });

    Promise.all(
      presetsToSave.map((preset) =>
        this.presetAdapter?.savePreset(key, preset),
      ),
    );
  }

  setEditingPreset(preset: ResourceTablePreset | null) {
    this.editingPreset.set(preset);
  }

  patchEditingPreset(value: Partial<{ id: string | null; name: string }>) {
    this.editingPresetValue.set({ ...this.editingPresetValue(), ...value });
  }

  async saveEditingPreset() {
    const preset = this.editingPreset();
    const value = this.editingPresetValue();
    const key = this.presetsKey;

    if (!preset?.id || !key) {
      return;
    }

    this.presetsLoading.set(true);

    try {
      const saved = await this.presetAdapter?.savePreset(key, {
        ...preset,
        name: value.name?.trim(),
      });

      if (saved) {
        toast({
          severity: 'success',
          summary: 'Aktualizované',
          detail: `Zobrazenie ${saved.name} bolo úspešne aktualizované`,
        });

        this.editingPreset.set(null);
        this.presets.update((presets) =>
          presets.map((p) => (p.id === saved.id ? saved : p)),
        );
      }
    } catch (error) {
      toast({
        severity: 'danger',
        summary: 'Nepodarilo sa premenovať zobrazenie',
        detail: errorToString(error),
      });
    } finally {
      this.presetsLoading.set(false);
    }
  }

  async saveNewPreset() {
    const key = this.presetsKey;

    if (!key) {
      toast({
        severity: 'danger',
        summary: 'Chyba',
        detail: 'Nie je možné uložiť zobrazenie',
      });
      return;
    }

    const data = this.getCurrentPresetData();
    const highestOrder = this.sortedPresets()[0]?.order ?? -1;

    const preset: Partial<ResourceTablePreset> = {
      name: this.newPresetForm.value.name?.trim(),
      data,
      order: highestOrder + 1,
    };

    this.presetsLoading.set(true);

    try {
      const newPreset = await this.presetAdapter?.savePreset(key, preset);
      if (newPreset) {
        this.activePresetHash.set(newPreset.hash);
        this.presets.update((presets) => [...presets, newPreset]);
        this.newPresetDialogVisible.set(false);
        this.newPresetForm.reset();
        toast({
          severity: 'success',
          summary: 'Zobrazenie uložené',
          detail: `Zobrazenie ${newPreset.name} bolo úspešne uložené`,
        });
      }
    } catch (error) {
      toast({
        severity: 'danger',
        summary: 'Nepodarilo sa uložiť zobrazenie',
        detail: errorToString(error),
      });
      throw error;
    } finally {
      this.refreshPresetHash();
      this.presetsLoading.set(false);
    }
  }

  async saveIntoPreset(preset: ResourceTablePreset) {
    const key = this.presetsKey;

    if (!preset.id || !key) {
      return;
    }

    const newData = this.getCurrentPresetData();

    this.presetsLoading.set(true);

    try {
      const saved = await this.presetAdapter?.savePreset(key, {
        ...preset,
        data: newData,
      });

      if (saved) {
        this.presets.update((presets) =>
          presets.map((p) => (p.id === saved.id ? saved : p)),
        );
        this.activePresetHash.set(saved.hash);
      }

      toast({
        severity: 'success',
        summary: 'Zobrazenie aktualizované',
        detail: `Zobrazenie ${preset.name} bolo úspešne aktualizované`,
      });
    } catch (error) {
      toast({
        severity: 'danger',
        summary: 'Nepodarilo sa aktualizovať zobrazenie',
        detail: errorToString(error),
      });
    } finally {
      this.presetsLoading.set(false);
    }
  }

  async deletePreset(preset: ResourceTablePreset) {
    const key = this.presetsKey;

    if (!preset.id || !key) {
      toast({
        severity: 'danger',
        summary: 'Nie je možné zmazať zobrazenie',
      });
      return;
    }

    this.presetsLoading.set(true);

    try {
      await this.presetAdapter?.deletePreset(key, preset);
      this.presets.update((presets) =>
        presets.filter((p) => p.id !== preset.id),
      );
      toast({
        severity: 'success',
        summary: 'Zobrazenie zmazané',
        detail: `Zobrazenie ${preset.name} bolo úspešne zmazané`,
      });
    } catch (error) {
      toast({
        severity: 'danger',
        summary: 'Nepodarilo sa zmazať zobrazenie',
        detail: errorToString(error),
      });
    } finally {
      this.presetsLoading.set(false);
    }
  }

  async loadPresets() {
    const key = this.presetsKey;

    if (!key) {
      return;
    }

    this.presetsLoading.set(true);

    try {
      const presets = await this.presetAdapter?.getPresets(key);
      this.presets.set(presets ?? []);
    } catch (error) {
      toast({
        severity: 'danger',
        summary: 'Nepodarilo sa načítať zobrazenia',
        detail: errorToString(error),
      });
      throw error;
    } finally {
      this.presetsLoading.set(false);
    }
  }

  async applyPreset(preset: ResourceTablePreset) {
    if (!preset.data) {
      return;
    }

    const data = preset.data;
    const columns = data.columns;

    if (columns) {
      const columnOrder = [...this._columns.map((c) => c.key)].sort((a, b) => {
        const indexA = columns.indexOf(a);
        const indexB = columns.indexOf(b);
        if (indexA === -1) {
          return 1;
        }
        if (indexB === -1) {
          return -1;
        }
        return indexA - indexB;
      });

      this.updateTableStateInLocalStorage((state) => {
        state.columnOrder = columnOrder;

        return state;
      });

      this.columnsVisibleState = this.columns.reduce<Record<string, boolean>>(
        (acc, c) => ({
          ...acc,
          [c.key]: columns.includes(c.key) ?? false,
        }),
        {},
      );
      this.saveExtraState();
      this.syncColumnsToTableState();
      this.dataTable?.restoreColumnOrder();
    }

    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const { perPage, sort, filters, search, ...rest } = this.query || {};

    this.query = {
      ...rest,
      filters: data.query.filters ?? '',
      sort: (data.query.sort ?? []).sort(),
      search: data.query.search ?? '',
      perPage: perPage ?? this.config.perPage,
      page: 1,
    };

    this.dataTable?.reset();

    this.handleQueryChange(this.query);

    this.activePresetHash.set(preset.hash ?? '');

    this.refreshPresetHash();
  }

  getCurrentPresetData(): ResourceTablePresetData {
    const { search, filters, sort } = this.query || {};

    const query = {
      search: search ?? '',
      filters: filters ?? '',
      sort: (sort ?? []).sort(),
    };

    const columns = (this.dataTable?.columns ?? this.columns)
      .map((c) => c.key)
      .filter((key) => {
        return this.columnsVisibleState[key];
      });

    return {
      query,
      columns,
    };
  }

  private refreshPresetHash() {
    this.refreshPresetHashStream$.next();
  }

  toggleSearch() {
    this.showSearch = !this.showSearch;
    if (this.showSearch) {
      setTimeout(() => {
        this.searchInput?.nativeElement?.focus();
      }, 0);
    }
  }

  private _config: TableFullConfig<T> = {
    sortable: true,
    paginator: false,
    perPage: 25,
    perPageOptions: [25, 50, 100],
    header: {
      newButton: {
        label: 'Pridať',
        routerLink: ['create'],
        icon: 'pi pi-plus',
      },
    },
  };

  @Input()
  set config(cfg: Partial<TableFullConfig<T>>) {
    this._config = {
      ...this._config,
      ...cfg,
      header: {
        ...this._config.header,
        ...cfg.header,
      },
    };

    this.hotfixCorruptedState();

    this.refreshActions();
    this.refreshTableData();
    this.refreshPerPageOptions();
    this.refreshPresetConfig();
  }
  get config(): TableFullConfig<T> {
    return this._config;
  }

  get multiSortMeta() {
    return this.tableQuery.multiSortMeta;
  }

  get filters() {
    this.automaticSetQuickFilter();
    return { ...this.dataTable?.filters, ...this.tableQuery.filters } as Record<
      string,
      FilterMetadata | FilterMetadata[]
    >;
  }

  currentQuickFilter?: IQuickFilter;

  quickFilterChange() {
    // Remove all filters from previous quick filter
    const quickFiltersKeys: Set<string> = new Set(
      (this.config.header?.quickFilters ?? []).flatMap((item: IQuickFilter) =>
        item.filters.map((f) => f.key),
      ),
    );
    quickFiltersKeys.forEach((key) => {
      delete this.tableQuery?.filters?.[key];
      if (this.dataTable?.filters[key]) {
        this.dataTable.filters[key] = [
          { matchMode: 'in', operator: 'and', value: null },
        ];
      }
    });

    if (this.currentQuickFilter?.filters?.some((f) => f.value !== undefined)) {
      const quickFilterObj = this.processQuickFilter(this.currentQuickFilter);
      this.tableQuery = {
        ...this.tableQuery,
        filters: {
          ...this.tableQuery?.filters,
          ...quickFilterObj,
        },
      };
    }

    this.handleQueryChange({
      ...this.queryAdapter.toApiQuery({
        ...this.tableQuery,
        filters: this.tableQuery?.filters,
      } as TableQuery),
    });
  }

  processQuickFilter(quickFilter: IQuickFilter) {
    const result: Record<string, FilterMetadata[]> = {};
    quickFilter.filters.forEach((f) => {
      result[f.key] = [
        {
          matchMode: 'in',
          value: Array.isArray(f?.value) ? f.value : [f?.value],
          operator: 'or',
        },
      ];
    });
    return result;
  }

  @ViewChild('settingsDialog', { static: true }) settingsDialog?: Dialog;

  showTableSettings(index = 0) {
    this.tableSettingsVisible.set(true);

    if (this.settingsDialog) {
      this.settingsDialog.onShow.pipe(take(1)).subscribe(() => {
        this.activeSettingsIndex.set(index);
      });
    } else {
      this.activeSettingsIndex.set(index);
    }
  }

  @ViewChild('splitButton') splitButton?: SplitButton;

  handleHeaderButtonClick(event: MouseEvent) {
    const btnConfig = this.config.header?.newButton;

    if (!btnConfig) {
      return;
    }

    if (btnConfig.routerLink) {
      this.router.navigate(btnConfig.routerLink, {
        relativeTo: this.route,
      });
    } else if (btnConfig.command) {
      btnConfig.command();
    } else if (this.splitButton) {
      setTimeout(() => this.splitButton?.onDropdownButtonClick(event));
    }
  }

  clearFilter(key: string) {
    const filters = { ...this.tableQuery.filters };
    delete filters[key];

    if (this.dataTable) {
      const meta = this.dataTable.filters?.[key];
      const matchMode = Array.isArray(meta)
        ? meta[0].matchMode
        : meta?.matchMode;

      this.dataTable.filters[key] = [
        {
          matchMode: matchMode ?? FilterMatchMode.CONTAINS,
          operator: 'and',
          value: null,
        },
      ];
    }

    const apiQuery = this.queryAdapter.toApiQuery({
      ...this.tableQuery,
      filters,
    });

    this.handleQueryChange(apiQuery);
    this.automaticSetQuickFilter();
  }

  clear(table: Table) {
    this.currentQuickFilter = this.getDefaultQuickFilter();
    const state: TableExtraState = {
      columnsVisibleState: this.columnsVisibleState,
      search: undefined,
    };
    if (this.stateExtraKey) {
      localStorage.setItem(this.stateExtraKey, JSON.stringify(state));
    }
    table.clear();
    this.query = null;
    this.queryChange.emit({
      ...(this.currentQuickFilter?.filters?.some((f) => f.value !== undefined)
        ? this.queryAdapter.toApiQuery({
            filters: { ...this.processQuickFilter(this.currentQuickFilter) },
          })
        : {}),
      search: undefined,
    });
    this.dataTable?.saveState();
  }

  reset() {
    if (this.dataTable) {
      this.clearPreset();
      this.dataTable?.reset();
      this.dataTable?.clearState();
      this.clearExtraState();
      this.dataTable.stateKey = undefined;
    }
    location.reload();
  }

  onLazyLoad(event: TableLazyLoadEvent) {
    this.handleQueryChange({
      ...this.queryAdapter.toApiQuery(event as TableQuery),
    });
  }

  onStateSave(state: TableState) {}

  private hotfixCorruptedState() {
    this.updateTableStateInLocalStorage((state) => {
      if (state.first == null) {
        state.first = 0;
      }
      if (state.rows == null) {
        state.rows = this.config.perPage || 25;
      }
      return state;
    });
  }

  onRowExpand(event: TableRowExpandEvent) {
    this.rowExpand.emit({
      ...event,
      data: event.data.data,
    });
  }

  onRowCollapse(event: TableRowCollapseEvent) {
    this.rowCollapse.emit({
      ...event,
      data: event.data.data,
    });
  }

  onStateRestore(state: TableState) {
    this.restoreRouteQuery(this.queryAdapter.toApiQuery(state));
  }

  onSearchChange(search: string | null) {
    this.handleQueryChange({ search: search ?? undefined, page: 1 });
    this.query = { ...this.query, search: search ?? undefined };
    this.saveExtraState();
  }

  onShowActiveFiltersChange(show: boolean) {
    this.showActiveFilters = show;
    this.saveCommonState();
  }

  columnFilterOperator(column: TableColumnSchema) {
    return dataGet(this.filters, `${column.key}.0.operator`, 'and');
  }

  columnLabel(column: TableColumnSchema) {
    return (
      column.label ??
      column.key
        .split('.')
        .map((p) => p[0].toUpperCase() + p.slice(1).toLowerCase())
        .join(' ')
    );
  }

  columnTemplate(column: TableColumnSchema) {
    return this.templates.find((t) => t.tableColumn === column.key)?.template;
  }

  columnSort(column: TableColumnSchema) {
    const isSortable =
      column.sortable !== undefined ? column.sortable : this.config.sortable;

    return isSortable ? column.key : undefined;
  }

  handleQueryChange(query: Partial<TableApiQuery>) {
    this.queryChange.emit({ ...this.query, ...query });

    this.zone.runOutsideAngular(() => {
      setTimeout(() => {
        this.dataTable?.saveState();
      });
    });
  }

  getCellFromRow(row: T, key: string) {
    return dataGet(row, key);
  }

  getFilterType(column: TableColumnSchema) {
    return typeof column.filter === 'object'
      ? column.filter.type
      : column.filter;
  }

  getFilterMatchMode(column: TableColumnSchema): string | undefined {
    const type = this.getFilterType(column);
    if (type === 'boolean') {
      return FilterMatchMode.EQUALS;
    }

    return undefined;
  }

  getFilterOptions(column: TableColumnSchema) {
    if (typeof column.filter === 'object' && column.filter.type === 'select') {
      if (column.filter.options instanceof Map) {
        return filterEnumOptions(
          column.filter.options,
          (k) => k !== '_default',
        );
      }
      return column.filter.options;
    }

    return undefined;
  }

  get hasRowActions() {
    return !!(this.actions.row?.length || this.actions.menu?.length);
  }

  automaticSetQuickFilter() {
    const tableFilters = this.tableQuery?.filters;
    const quickFilters = [...(this.config.header?.quickFilters ?? [])];

    if (!quickFilters) return;

    const quickFilter = quickFilters
      .sort((a, b) => (b.filters?.length ?? 0) - (a.filters?.length ?? 0))
      .find((q) => {
        return q.filters.every((f) => {
          const filters = tableFilters?.[f.key];
          if (
            (!filters || !Array.isArray(filters)) &&
            (f.value === undefined || f.value === null)
          )
            return true;

          if (!filters || !Array.isArray(filters)) return false;

          return filters.some((filter) => {
            const processedFilter = this.processQuickFilter({
              ...q,
              filters: [f],
            })?.[f.key]?.[0];

            const pfv = Array.isArray(processedFilter.value)
              ? processedFilter.value
              : [processedFilter.value];
            const fv = Array.isArray(filter.value)
              ? filter.value
              : [filter.value];
            if (pfv.length !== fv.length) return false;
            return pfv.every((v) => fv.includes(v));
          });
        });
      });

    if (quickFilter) {
      this.currentQuickFilter = quickFilter;
    } else {
      this.currentQuickFilter =
        quickFilters?.find((qf) => qf.filters?.every((f) => !f.value)) ||
        undefined;
    }
  }

  getDefaultQuickFilter() {
    const quickFilters = this.config.header?.quickFilters;

    if (!quickFilters) return;
    let defaultQuickFilter = quickFilters.find(
      (filter) => filter.default == true,
    );
    if (!defaultQuickFilter) {
      defaultQuickFilter = quickFilters[0];
    }
    return defaultQuickFilter;
  }

  processDefaultAction(row: T) {
    return this.processAction(this.actions.default, row);
  }

  isColumnVisible(column: TableColumnSchema) {
    return this.columnsVisibleState[column.key] ?? false;
  }

  onColumnVisibleChange(column: TableColumnSchema, visible: boolean) {
    this.columnsVisibleState[column.key] = visible;
    this.saveExtraState();
    this.refreshPresetHash();
  }

  private refreshTableQuery() {
    this.tableQuery = this.queryAdapter.toTableQuery(this.query, {
      filterTypes: this.columns.reduce<{ [k: string]: FilterType | undefined }>(
        (acc, c) => ({ ...acc, [c.key]: this.getFilterType(c) }),
        {},
      ),
    });

    this.searchControl.setValue(this.query?.search ?? '', { emitEvent: false });
  }

  private refreshSelectedData() {
    const selectedIds = this.selectedIds();
    const dataIds = new Set(
      this.data?.map((row) =>
        typeof this.config.dataKey === 'function'
          ? this.config.dataKey(row)
          : dataGet(row, this.config.dataKey ?? 'id'),
      ) ?? [],
    );

    const newSelectedIds = new Set<string>(
      [...selectedIds].filter((id) => dataIds.has(id)),
    );

    if (newSelectedIds.size !== selectedIds.size) {
      this.selectedIds.set(newSelectedIds);
      this._lastSelectedIndex = null;
    }
  }

  private refreshTableData() {
    const scrollPositionTop = this.getCurrentScrollTopPosition();
    this.tableData =
      this.data?.map((row) => ({
        id:
          typeof this.config.dataKey === 'function'
            ? this.config.dataKey(row)
            : dataGet(row, this.config.dataKey ?? 'id'),
        data: row,
        menuItems: this.processMenuActions(row),
        rowActions: this.processRowActions(row),
      })) ?? [];

    setTimeout(() => {
      this.restoreScrollTopPosition(scrollPositionTop);
    }, 0);
  }

  private restorePreset() {
    this.refreshPresetConfig();

    const stateKey = this.statePresetKey;

    if (!stateKey) {
      return;
    }

    const savedLastPresetHash = this.getState<string>(stateKey);

    if (savedLastPresetHash) {
      this.activePresetHash.set(savedLastPresetHash);
    }
  }

  private clearPreset() {
    this.activePresetHash.set('');
    const stateKey = this.statePresetKey;
    if (!stateKey) {
      return;
    }

    this.clearState(stateKey);
  }

  private restoreRouteQuery(oldQuery?: TableApiQuery) {
    this.query = {
      ...this.query,
      ...oldQuery,
      ...this._routeQuery,
    };

    this.handleQueryChange(this.query);
  }

  private syncColumnsToTableState() {
    this.updateTableStateInLocalStorage((state) => {
      const defaultOrder = this._columns.map((c) => c.key);
      state.columnOrder = this._columns
        .sort((a, b) => {
          let indexA = state.columnOrder?.indexOf(a.key) ?? -1;
          let indexB = state.columnOrder?.indexOf(b.key) ?? -1;
          if (indexA === -1) {
            indexA = defaultOrder.indexOf(a.key);
          }
          if (indexB === -1) {
            indexB = defaultOrder.indexOf(b.key);
          }
          return indexA - indexB;
        })
        .map((c) => c.key);
      return state;
    });
  }

  private updateTableStateInLocalStorage(
    map: (state: TableState) => TableState,
  ) {
    if (this.stateKey) {
      const state = this.getState<TableState>(this.stateKey);
      if (state) {
        const newState = map(state);
        localStorage.setItem(this.stateKey, JSON.stringify(newState));
      }
    }
  }

  private saveExtraState() {
    if (!this.stateExtraKey) {
      return;
    }

    const state: TableExtraState = {
      columnsVisibleState: this.columnsVisibleState,
      search: this.query?.search,
    };

    localStorage.setItem(this.stateExtraKey, JSON.stringify(state));

    this.zone.runOutsideAngular(() => {
      setTimeout(() => {
        this.dataTable?.saveState();
      });
    });
  }

  private restoreExtraState() {
    if (!this.stateExtraKey) {
      return;
    }

    const state = this.getState<TableExtraState>(this.stateExtraKey);
    this.columnsVisibleState = {
      ...Object.fromEntries(this.columns.map((c) => [c.key, !c.hidden])),
      ...(state?.columnsVisibleState ?? {}),
    };
    this.query = { ...this.query, search: state?.search };
  }

  setState<T>(key: string, state: T) {
    localStorage.setItem(key, JSON.stringify(state));
  }

  getState<T>(key?: string | null): T | undefined {
    if (!key) {
      return undefined;
    }
    const stateStr = localStorage.getItem(key);
    return stateStr ? (JSON.parse(stateStr) as T) : undefined;
  }

  clearState(key: string) {
    localStorage.removeItem(key);
  }

  private clearExtraState() {
    if (!this.stateExtraKey) {
      return;
    }

    localStorage.removeItem(this.stateExtraKey);
  }

  private saveCommonState() {
    const state: TableCommonState = {
      showActiveFilters: this.showActiveFilters,
    };

    localStorage.setItem(this.stateCommonKey, JSON.stringify(state));
  }

  private restoreCommonState() {
    const state = this.getState<TableCommonState>(this.stateCommonKey);

    if (state) {
      this.showActiveFilters = state.showActiveFilters;
    }
  }

  private processRowActions(row: T) {
    return (this.actions?.row
      ?.map((action) => this.processAction(action, row))
      ?.filter((action) => !!action) ?? []) as ProcessedAction<T>[];
  }

  private processMenuActions(row: T) {
    return (
      this.actions?.menu
        ?.map((action) => this.processAction(action, row))
        ?.filter((action) => !!action)
        ?.map((action) => this.toMenuItem(action!, row)) ?? []
    );
  }

  private processContextMenuActions(row: T) {
    const items =
      this.actions.contextMenu
        ?.map((action) => this.processAction(action, row))
        ?.filter((action) => !!action)
        ?.map((action) => this.toMenuItem(action!, row)) ?? [];

    if (items.length === 0) {
      return undefined;
    }

    return items;
  }

  private processAction(
    action: ActionWithoutContext<T> | undefined,
    row: T,
  ): ProcessedAction<T> | undefined {
    if (!action) {
      return action;
    }

    const functionLikes: (keyof ActionWithoutContext<T>)[] = ['command'];

    const processed = Object.fromEntries(
      Object.entries(action).map(([k, v]) => {
        // if the value is a function, we need to call it with the row
        if (typeof v === 'function') {
          // we need to wrap the function inside another function to make sure it's not called immediately
          if (functionLikes.includes(k as keyof ActionWithoutContext<T>)) {
            return [k, () => v(row)];
          }

          return [k, v(row)];
        }

        return [k, v];
      }),
    ) as ProcessedAction<T>;

    if (processed.visible === false) {
      return undefined;
    }

    return processed;
  }

  private toMenuItem(action: ProcessedAction<T>, row: T): MenuItem {
    return action;
  }

  onContextMenuHide() {
    this.selectedRow = null;
  }

  // ─── Bulk selection methods ──────────────────────────────────────────────────

  isRowSelected(row: T): boolean {
    return this.selectedIds().has(this.getRowId(row));
  }

  toggleRowSelection(row: T, event: MouseEvent): void {
    const index = this._data.indexOf(row);

    if (event.shiftKey && this._lastSelectedIndex !== null) {
      const from = Math.min(this._lastSelectedIndex, index);
      const to = Math.max(this._lastSelectedIndex, index);
      const rangeRows = this._data.slice(from, to + 1);

      // Determine intent: if the anchor row is selected, select the range; otherwise deselect it
      const anchorSelected = this.selectedIds().has(
        this.getRowId(this._data[this._lastSelectedIndex]),
      );
      const next = new Set(this.selectedIds());
      for (const r of rangeRows) {
        if (anchorSelected) {
          next.add(this.getRowId(r));
        } else {
          next.delete(this.getRowId(r));
        }
      }
      this.selectedIds.set(next);
      // Don't update anchor on shift-click
    } else {
      const id = this.getRowId(row);
      const next = new Set(this.selectedIds());
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      this.selectedIds.set(next);
      this._lastSelectedIndex = index;
    }
  }

  toggleAllSelection(): void {
    if (this.allRowsSelected) {
      this.selectedIds.set(new Set());
    } else {
      this.selectedIds.set(new Set(this._data.map((r) => this.getRowId(r))));
    }
  }

  clearBulkSelection(): void {
    this.selectedIds.set(new Set());
    this._lastSelectedIndex = null;
  }

  resolveBulkActionColor(action: BulkAction<T>): string {
    return action.color ?? 'primary';
  }

  cancelBulkAction(): void {
    if (this.bulkActionsRunning()) {
      this.bulkCancellationRequested = true;
    }
  }

  async executeBulkAction(action: BulkAction<T>): Promise<void> {
    if (this.bulkActionsRunning()) {
      return;
    }

    const ids = this.selectedIds();
    const rows = this._data.filter((r) => ids.has(this.getRowId(r)));
    if (rows.length === 0) {
      return;
    }

    const clearSelectionFn = () => {
      this.clearBulkSelection();
    };

    const confirmation = action.confirm?.({
      rows,
      clearSelection: clearSelectionFn,
    });

    const confirmFn = async () => {
      return new Promise<boolean>((resolve) => {
        if (confirmation) {
          confirmDialog({
            ...confirmation,
            accept: () => resolve(true),
            reject: () => resolve(false),
          });
        } else {
          resolve(true);
        }
      });
    };

    const confirmed = await confirmFn();

    if (!confirmed) {
      return;
    }

    this.bulkActionsRunning.set(true);
    this.bulkCancellationRequested = false;
    this.bulkProgress.set({ current: 0, total: rows.length });

    const results: { row: T; result: any }[] = [];
    const errors: { row: T; error: any }[] = [];

    try {
      const stopOnError = action.stopOnError ?? true;

      for (let i = 0; i < rows.length; i++) {
        if (this.bulkCancellationRequested) {
          break;
        }

        this.bulkProgress.set({ current: i + 1, total: rows.length });

        try {
          let result: any;
          const cmd = action.command(rows[i]);
          if (cmd instanceof Promise) {
            result = await cmd;
          } else {
            result = cmd;
          }

          results.push({ row: rows[i], result });
        } catch (error) {
          errors.push({ row: rows[i], error });
          if (stopOnError) {
            throw error;
          }
          continue;
        }
      }

      const thenResult = action.then?.({
        rows,
        processed: results,
        errors,
        clearSelection: clearSelectionFn,
      });

      if (thenResult instanceof Promise) {
        await thenResult;
      }
    } catch (err) {
      const catchResult = action.catch?.(err, {
        rows,
        processed: results,
        errors,
        clearSelection: clearSelectionFn,
      });
      if (catchResult instanceof Promise) {
        await catchResult;
      }
    } finally {
      const finallyResult = action.finally?.({
        rows,
        processed: results,
        errors,
        clearSelection: clearSelectionFn,
      });
      if (finallyResult instanceof Promise) {
        await finallyResult;
      }
      this.bulkActionsRunning.set(false);
      this.bulkProgress.set(null);
      this.bulkCancellationRequested = false;
    }
  }

  private getRowId(row: T): string {
    return typeof this.config.dataKey === 'function'
      ? this.config.dataKey(row)
      : String(dataGet(row, this.config.dataKey ?? 'id'));
  }

  // ────────────────────────────────────────────────────────────────────────────

  get total() {
    return this.meta?.total ?? this.data?.length ?? 0;
  }

  get first() {
    return (this.meta?.from ?? 1) - 1;
  }

  get perPage() {
    return this.meta?.perPage ?? this.config.perPage ?? 0;
  }

  get hasActiveFilters() {
    return (
      !!this.query?.search?.length || !!this.query?.filters?.toString().length
    );
  }

  get stateKey() {
    return this.config.stateKey
      ? `${this.stateKeyPrefix}table:${this.config.stateKey}`
      : undefined;
  }

  get stateExtraKey() {
    return this.config.stateKey
      ? `${this.stateKeyPrefix}table/extra:${this.config.stateKey}`
      : undefined;
  }

  get stateCommonKey() {
    return `${this.stateKeyPrefix}table/common`;
  }

  get isExpandable() {
    return this.config.expandMode != null;
  }

  get statePresetKey() {
    return this.config.stateKey
      ? `${this.stateKeyPrefix}table/preset:${this.config.stateKey}`
      : undefined;
  }

  get presetsKey() {
    return this.config.presetsKey || this.config.stateKey;
  }

  private refreshPresetConfig() {
    const presetsKey = this.presetsKey;
    this.presetsEnabled.set(
      !!presetsKey &&
        !!this.presetAdapter &&
        this.config.presetsDisabled !== true,
    );
  }

  private refreshPerPageOptions() {
    this.perPageOptions = [
      this.perPage,
      ...this.config.perPageOptions.filter((pp) => pp != this.perPage),
    ];
  }

  private refreshActions() {
    this.actions = Object.fromEntries(
      Object.entries(
        this._config.actions?.reduce((acc, action) => {
          const contexts: TableActionContext[] = [];

          if (!Array.isArray(action.context)) {
            if (action.context === 'all') {
              contexts.push(...ALL_TABLE_ACTION_CONTEXTS);
            } else {
              contexts.push(action.context);
            }
          } else {
            contexts.push(...action.context);
          }

          // add all actions from row to menu also
          if (contexts.includes('row') && !contexts.includes('menu')) {
            contexts.push('menu');
          }

          contexts.forEach((ctx) => {
            if (ctx === 'default') {
              if (acc[ctx]) {
                throw new Error(`Multiple default actions are not supported`);
              }

              acc['default'] = action;
            } else {
              if (!acc[ctx] || !Array.isArray(acc[ctx])) {
                acc[ctx] = [];
              }

              acc[ctx]?.push(action);
            }
          });

          return acc;
        }, {} as CategorizedActions<T>) ?? {},
      ),
    );
  }

  onContextMenuSelectionChange(event: T) {
    this.selectedRow = event;
    this.contextMenuItems = this.processContextMenuActions(event);
  }

  isMobile = signal(false);

  constructor() {
    this.searchControl.valueChanges
      .pipe(
        debounceTime(300),
        // ignore whitespace changes
        distinctUntilChanged((previous, current) => {
          return previous?.trim() === current?.trim();
        }),
        takeUntilDestroyed(),
      )
      .subscribe((search) => this.onSearchChange(search));

    this._routeQuery = window.history.state?.query ?? null;

    if (this._routeQuery) {
      // Remove table query from history state - if we dont do this, and user
      // refreshes the page, table will set filters again from the history
      // state, even though the filters could be changed at that point
      window.history.replaceState(
        { ...window.history.state, query: null },
        '',
        window.location.pathname,
      );
    }

    effect(() => {
      const hash = this.activePresetHash();
      const key = this.statePresetKey;

      if (key && hash) {
        this.setState<string>(key, hash);
      }
    });

    effect(
      () => {
        const editingPreset = this.editingPreset();

        this.editingPresetValue.set({
          id: editingPreset?.id ?? null,
          name: editingPreset?.name ?? '',
        });
      },
      { allowSignalWrites: true },
    );

    this.breakpointObserver
      .observe('(max-width: 1200px)')
      .pipe(takeUntilDestroyed())
      .subscribe((state) => {
        this.isMobile.set(state.matches);
      });
  }

  private getCurrentScrollTopPosition() {
    const body = this.dataTable?.el?.nativeElement?.querySelector(
      '.p-datatable-wrapper',
    );

    return body?.scrollTop;
  }

  private restoreScrollTopPosition(position: number) {
    const body = this.dataTable?.el?.nativeElement?.querySelector(
      '.p-datatable-wrapper',
    );

    if (body) {
      body.scrollTop = position;
    }
  }

  rowClasses(rowData: any) {
    if (rowData.deletedAt) {
      return 'group/row bg-danger-50 hover:bg-danger-100';
    }
    return 'group/row hover:bg-gray-100';
  }

  ngOnInit() {
    this.restoreExtraState();
    this.restoreCommonState();
    this.restoreRouteQuery();
    this.restorePreset();
  }

  ngAfterViewInit(): void {
    this.refreshTableQuery();
    this.refreshPresetHash();

    if (this._routeQuery) {
      this.clearPreset();
      this.query = { ...this._routeQuery };
      this._routeQuery = null;
      this.handleQueryChange(this.query);
    }
  }

  ngOnChanges(changes: SimpleChanges) {
    const configChange = changes['config'];

    if (configChange) {
      const previous: TableFullConfig<T> | undefined =
        configChange.previousValue;
      const current: TableFullConfig<T> = configChange.currentValue;

      if (previous?.stateKey !== current.stateKey) {
        this.loadPresets();
      }
    }
  }

  onColReorder(event: TableColumnReorderEvent) {
    this.refreshPresetHash();
  }
}

export interface TableDataMeta {
  total?: number;
  perPage?: number;
  currentPage?: number;
  lastPage?: number;
  from?: number;
  to?: number;
}

export interface TableColumnSchema {
  /**
   * Key of the data column
   * Can be in "dot" notation, for example "user.name"
   */
  key: string;

  /**
   * Display name of the column
   * If not provided, will be generated from the key
   * For example, "user.name" will be "User Name"
   */
  label?: string;

  /**
   * Subtitle of the column
   *
   * If provided, will be displayed below the label
   */
  subLabel?: string;

  /**
   * Filter type for the column
   */
  filter?: FilterSchema;

  /**
   * Is column sotable
   * Inherits value from table config
   * This option overrides global table config
   */
  sortable?: boolean;

  /**
   * Options for the display values of the column
   * Can be either array of label and values or map of EnumOptions
   */
  options?:
    | {
        label: string;
        value: any;
      }[]
    | Map<string, EnumOptions>;

  /**
   * Format function for the column
   * If provided, will be used to format the value
   * If not provided, value will be used as is
   * @param value - value of the column
   * @returns formatted value
   * @example
   * ```ts
   * format: (value) => value?.toUpperCase()
   * ```
   */
  format?: (value: any | null, row: any) => string;

  /**
   * Custom css classes for the column
   */
  class?: string;

  /**
   * If the column should be hidden by default
   */
  hidden?: boolean;
}

export type FilterType = 'text' | 'date' | 'boolean' | 'numeric' | 'select';
export interface SelectFilterSchema {
  type: 'select';

  /**
   * Options for the select filter
   * Can be either array of label and values or map of EnumOptions
   */
  options:
    | {
        label: string;
        value: any;
      }[]
    | Map<string, EnumOptions>;

  /**
   * If using EnumOptions Map, what value should be used when "_default" is selected
   * @default "" (empty string)
   */
  defaultValue?: any;

  /**
   * If using EnumOptions Map, should the default option ("_default") be hidden
   * @default false
   */
  hideDefault?: boolean;
}

export interface TextFilterSchema {
  type: 'text';
}

export interface DateFilterSchema {
  type: 'date';
}

export interface BooleanFilterSchema {
  type: 'boolean';
}

export interface NumericFilterSchema {
  type: 'numeric';
}

export type FilterSchema =
  | FilterType
  | SelectFilterSchema
  | TextFilterSchema
  | DateFilterSchema
  | BooleanFilterSchema
  | NumericFilterSchema;

export type TableColumns = TableColumnSchema[];

export interface TableFullConfig<T> {
  /**
   * Adds sortable header
   * Can be overriden on indivual columns
   */
  sortable: boolean;

  /**
   * Adds paginator to the table
   * If paginated data is provided, this is set to true
   */
  paginator: boolean;

  /**
   * Per page options for paginator
   */
  perPageOptions: number[];

  /**
   * Number of records per page
   * If paginated data is provided to the table,
   * per_page from meta is used
   */
  perPage: number;

  header?: {
    newButton?: IButton | false;
    actions?: MenuItem[];
    quickActions?: MenuItem[];
    quickFilters?: IQuickFilter[];
  };

  actions?: TableAction<T>[];

  /**
   * Bulk actions executed row-by-row on selected rows from the frontend.
   * Requires `dataKey` to be set for row identity tracking.
   */
  bulkActions?: BulkAction<T>[];

  /**
   * If provided, table state will be saved in local storage
   */
  stateKey?: string;

  /**
   * If provided, table will be expandable
   */
  expandMode?: 'single' | 'multiple';

  /**
   * Data key to be used for tracking rows - should be unique
   */
  dataKey?: string | ((row: any) => string);

  /**
   * Key for presets, if not provided, stateKey is used
   */
  presetsKey?: string;

  /**
   * Flag whether presets are explicitly disabled
   */
  presetsDisabled?: true;
}

export type TableConfig<T = any> = Partial<TableFullConfig<T>>;

type TableProcessedData<T> = {
  data: T;
  menuItems?: MenuItem[];
  rowActions?: ProcessedAction<T>[];
}[];

export interface IQuickFilter {
  label: string;
  filters: {
    key: string;
    value?: number | string | (string | number)[];
  }[];
  default?: true;
}

type TableExtraState = {
  columnsVisibleState?: Record<string, boolean>;
  search?: string;
};

type TableCommonState = {
  showActiveFilters: boolean;
};

export type TableActiveFilters = {
  [key: string]: { label: string; count: number };
};
